<script setup>
/**
 * A etiqueta aplicada, com o "×" para tirar — a mesma no contato e na
 * conversa. Ponto e fundo na cor que o escritório escolheu, nome em
 * MAIÚSCULAS (Victor, 16/09/2026). O "×" fica sempre à vista: escondido
 * atrás do cursor ele não existia no celular.
 */
import { useI18n } from 'vue-i18n';

const props = defineProps({
  etiqueta: { type: Object, required: true },
  removivel: { type: Boolean, default: true },
});

const emit = defineEmits(['remover']);
const { t } = useI18n();

const nome = () => props.etiqueta.title.replace(/-/g, ' ').toUpperCase();
</script>

<template>
  <span
    v-tooltip.top="etiqueta.description || null"
    class="cde"
    :style="{ '--etq': etiqueta.color }"
  >
    <span class="cde__ponto" aria-hidden="true" />
    <span class="cde__nome">{{ nome() }}</span>
    <button
      v-if="removivel"
      type="button"
      class="cde__tirar"
      :aria-label="t('LABEL.REMOVE', { nome: nome() })"
      @click="emit('remover', etiqueta)"
    >
      <i class="i-lucide-x" />
    </button>
  </span>
</template>

<style scoped>
.cde {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  max-width: 100%;
  height: 24px;
  padding: 0 var(--esp-1) 0 var(--esp-2);
  border-radius: var(--r-redondo);
  background: color-mix(in srgb, var(--etq) 14%, rgb(var(--solid-1)));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-12));
  white-space: nowrap;
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.cde__ponto {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: var(--r-redondo);
  background: var(--etq);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--etq) 25%, transparent);
}

.cde__nome {
  overflow: hidden;
  text-overflow: ellipsis;
}

.cde__tirar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.cde__tirar > i {
  width: 11px;
  height: 11px;
}

.cde__tirar:hover {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}

.cde__tirar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

/* A SAÍDA (24/10/2026): quem lista os chips usa `<TransitionGroup name="cde">` e o chip
   removido recolhe a largura no lugar. Mora aqui para as duas listas (contato e conversa)
   não terem cada uma a sua. Recolhe em vez de `position: absolute`, que faria o chip saltar
   para o início da fileira; duas classes vencem a entrada (`.cde`) sem depender de ordem. */
.cde.cde-leave-active {
  overflow: hidden;
  pointer-events: none;
  animation: cdeSai var(--mov-2) var(--mov-curva-saida) forwards;
}

@keyframes cdeSai {
  from {
    max-width: 16rem;
  }

  to {
    max-width: 0;
    padding-inline: 0;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cde,
  .cde.cde-leave-active {
    animation: none;
  }
}
</style>
